import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { AddCircleOutlineRounded, ExpandMore } from '@mui/icons-material';
import DeleteForeverOutlined from '@mui/icons-material/DeleteForeverOutlined';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import moment from 'moment';

import AcousticConfiguration from 'features/residents/models/configurations/acoustic-configuration';
import AcousticTimeFrameConfiguration from 'features/residents/models/configurations/acoustic-time-frame-configuration';
import { useAcousticTimeFrames } from 'features/residents/resident-settings/components/resident-setting-details-scenario-rows/acoustic-scenario-configuration/acoustic-time-frame-configuration/hooks';
import AcousticSettings from '../acoustic-settings/acoustic-settings';
import styles from './acoustic-time-frames.module.scss';

type Props = {
  acousticConfiguration: AcousticConfiguration;
  activeAllDay: boolean;
  soundThreshold: number;
  duration: number;
  valuesChanged: (newTimeFrameConfigurations: AcousticTimeFrameConfiguration[] | null, newActiveAllDay: boolean) => void;
  index: number;
  timeFramesDescription: string;
  updateResidentSettingEditFormScenario: () => void;
};

const AcousticTimeFrames = (props: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('residentsSettings');
  const {
    viewingMode,
    validationErrors,
    timePickerLocaleText,
    addTimeFrameConfiguration,
    removeTimeFrameConfiguration,
    onTimeFrameChanged,
    setAllDayTimeFrame,
    expandTimeFrames,
    toggleExpandTimeFrames,
  } = useAcousticTimeFrames(props);

  const timerPickerPopperSx = {
    '& .MuiPaper-root .MuiPickersLayout-root .MuiPickersLayout-contentWrapper .MuiMultiSectionDigitalClock-root': {
      padding: '4px',
      borderRadius: 0,
      minWidth: 0,
      margin: 0,
      marginLeft: 'auto',
      marginRight: 'auto',
      '& .MuiList-root': {
        width: '92px',
        overflowY: 'visible',
        '&::after': {
          height: '10px',
        },
        '& .MuiButtonBase-root': {
          padding: '4px',
          borderRadius: 0,
          minWidth: 0,
          margin: 0,
          marginLeft: 'auto',
          marginRight: 'auto',
        },
      },
    },
  };

  const renderTimeFrameRow = (timeFrameConfiguration: AcousticTimeFrameConfiguration, index: number) => (
    <Fragment key={`TimeFrame-${index}`}>
      <Accordion expanded={expandTimeFrames[index]} disableGutters>
        <AccordionSummary
          component="div"
          sx={{
            padding: 0,
            margin: '8px 0 0 0',
            width: '100%',
            '.MuiAccordionSummary-content': { margin: '0', width: '90%' },
            '.MuiAccordionSummary-expandIconWrapper': {
              marginTop: '16px',
              marginLeft: '16px',
              marginBottom: validationErrors[index] ? '20px' : '0',
            },
            '&.Mui-focusVisible': {
              backgroundColor: 'inherit',
            },
            flexDirection: 'row',
          }}
          expandIcon={<ExpandMore onClick={() => toggleExpandTimeFrames(index)} />}
          aria-label="Expand"
          aria-controls="additional-actions1-content"
        >
          <div className={styles.accordionHeaderContainer}>
            <div className={styles.timePickers}>
              <TimePicker
                label={t('scenarioConfiguration.from')}
                ampm={false}
                view={'minutes'}
                views={['hours', 'minutes']}
                format="HH:mm"
                localeText={timePickerLocaleText}
                value={moment(timeFrameConfiguration.timeFrame.from, 'HH:mm:ss')}
                onChange={(value) => onTimeFrameChanged(value, index, true)}
                disabled={viewingMode === 'viewing' || props.acousticConfiguration.temporaryConfigurationEnabled}
                className={`${styles.timePicker}${validationErrors[index] ? ' ' + styles.validationError : ''}`}
                slotProps={{
                  popper: {
                    sx: timerPickerPopperSx,
                  },
                }}
              />
              <TimePicker
                label={t('scenarioConfiguration.to')}
                ampm={false}
                view={'minutes'}
                views={['hours', 'minutes']}
                format="HH:mm"
                localeText={timePickerLocaleText}
                value={moment(timeFrameConfiguration.timeFrame.to, 'HH:mm:ss')}
                onChange={(value) => onTimeFrameChanged(value, index, false)}
                disabled={viewingMode === 'viewing' || props.acousticConfiguration.temporaryConfigurationEnabled}
                className={`${styles.timePicker}${validationErrors[index] ? ' ' + styles.validationError : ''}`}
                slotProps={{
                  popper: {
                    sx: timerPickerPopperSx,
                  },
                }}
              />
              {viewingMode !== 'viewing' &&
                !props.acousticConfiguration.temporaryConfigurationEnabled &&
                props.acousticConfiguration.acousticTimeFrameConfigurations &&
                props.acousticConfiguration.acousticTimeFrameConfigurations.length > 1 && (
                  <DeleteForeverOutlined
                    onClick={() => removeTimeFrameConfiguration(index)}
                    className={`${styles.deleteIcon}${validationErrors[index] ? ' ' + styles.validationError : ''}`}
                  />
                )}
            </div>
            {validationErrors[index] && (
              <div className={styles.validationText}>
                <Typography variant="caption" className={styles.typographyColor}>
                  {t('scenarioConfiguration.timeFrameValidation')}
                </Typography>
              </div>
            )}
          </div>
        </AccordionSummary>

        <AccordionDetails sx={{ padding: '0 0 8px 0', margin: 0 }}>
          <AcousticSettings
            acousticConfiguration={props.acousticConfiguration}
            activeAllDay={props.activeAllDay}
            index={index}
            valuesChanged={props.valuesChanged}
            updateResidentSettingEditFormScenario={props.updateResidentSettingEditFormScenario}
            isTemporaryConfiguration={false}
          />
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );

  return (
    <div className={styles.timeFramesContainer}>
      <div className={styles.timeFramesRow}>
        <Typography variant="subtitle1">{props.timeFramesDescription}</Typography>

        <FormControl
          sx={{
            minWidth: 0,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <Checkbox checked={props.activeAllDay} onChange={setAllDayTimeFrame} disabled={viewingMode === 'viewing'} />
          <Typography variant="subtitle1">{t('scenarioConfiguration.scenarioAlwaysActive')}</Typography>
        </FormControl>

        {!props.activeAllDay && props.acousticConfiguration.acousticTimeFrameConfigurations?.map(renderTimeFrameRow)}

        {viewingMode !== 'viewing' && !props.activeAllDay && !props.acousticConfiguration.temporaryConfigurationEnabled && (
          <Grid
            className={styles.addTimeFrame}
            onClick={addTimeFrameConfiguration}
            onKeyDown={(e) => e.key === 'Enter' && addTimeFrameConfiguration()}
          >
            <AddCircleOutlineRounded />
            <Typography variant="body1">{t('scenarioConfiguration.addTimeFrame')}</Typography>
          </Grid>
        )}
      </div>
    </div>
  );
};

export default AcousticTimeFrames;
